import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Shield, Activity, Users, Clock, Star, ChevronRight, Phone, AlertTriangle, MapPin, FileText, Heart, Settings, Bot, UserCircle } from "lucide-react";
import TrustBadgeDisplay from "@/components/TrustBadgeDisplay";
import ClinicalDisclaimerPanel from "@/components/ClinicalDisclaimerPanel";
import AmIOKScoreExplanation from "@/components/home/AmIOKScoreExplanation";
import { useAuth } from "@/hooks/useAuth";
const Index = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    user,
    signOut
  } = useAuth();
  const handleStartAssessment = () => {
    navigate("/assessment");
  };
  const handleEmergencyCall = () => {
    window.location.href = "tel:999";
  };
  const handleAuthAction = async () => {
    if (user) {
      await signOut();
    } else {
      navigate("/auth");
    }
  };
  const handleNHSGPs = () => {
    window.open("https://www.nhs.uk/service-search/find-a-gp", "_blank");
  };
  const handleMentalHealthSupport = () => {
    window.open("https://www.nhs.uk/mental-health/", "_blank");
  };
  const quickActions = [{
    icon: Activity,
    label: "Health Check",
    description: "Quick symptom assessment",
    action: () => navigate("/assessment")
  }, {
    icon: Clock,
    label: "History",
    description: "View past assessments",
    action: () => navigate("/history"),
    requiresAuth: true
  }, {
    icon: MapPin,
    label: "Find NHS GPs",
    description: "Locate nearby doctors",
    action: handleNHSGPs
  }, {
    icon: Heart,
    label: "Mental Health",
    description: "NHS talking therapies",
    action: handleMentalHealthSupport
  }, {
    icon: Phone,
    label: "Emergency",
    description: "Urgent medical help",
    action: handleEmergencyCall,
    urgent: true
  }].filter(action => !action.requiresAuth || user);
  return <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            <div className="bg-primary p-2 rounded-lg">
              <Shield className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">AmIOk</h1>
              <p className="text-xs text-muted-foreground">Health Pre-Consultant</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={handleEmergencyCall} className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground">
              <Phone className="h-4 w-4 mr-1" />
              999
            </Button>
            
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <UserCircle className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                  <SheetDescription>
                    Access all app features and information
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                  {user ? <>
                      <Button variant="ghost" className="w-full justify-start" onClick={() => {
                    navigate("/account-settings");
                    setIsMenuOpen(false);
                  }}>
                        <Settings className="mr-2 h-4 w-4" />
                        Account Settings
                      </Button>
                      <Button variant="ghost" className="w-full justify-start" onClick={() => {
                    navigate("/history");
                    setIsMenuOpen(false);
                  }}>
                        <Clock className="mr-2 h-4 w-4" />
                        Assessment History
                      </Button>
                      <Button variant="ghost" className="w-full justify-start" onClick={() => {
                    handleAuthAction();
                    setIsMenuOpen(false);
                  }}>
                        <Users className="mr-2 h-4 w-4" />
                        Sign Out
                      </Button>
                    </> : <Button variant="ghost" className="w-full justify-start" onClick={() => {
                  navigate("/auth");
                  setIsMenuOpen(false);
                }}>
                      <Users className="mr-2 h-4 w-4" />
                      Sign In / Register
                    </Button>}
                  <Button variant="ghost" className="w-full justify-start" onClick={() => {
                  navigate("/privacy-policy");
                  setIsMenuOpen(false);
                }}>
                    <Shield className="mr-2 h-4 w-4" />
                    Privacy Policy
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 space-y-6">
        {/* Main CTA */}
        <Card className="p-6 text-center bg-primary/5 border-primary/20">
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
              <Activity className="h-8 w-8 text-primary-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Are You OK?</h2>
            <p className="text-muted-foreground mb-6">
              Get personalized health guidance with our AI-powered symptom assessment tool.
            </p>
          </div>
          
          <Button size="lg" onClick={handleStartAssessment} className="w-full h-12 text-lg">
            Begin Assessment
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
          
          <div className="flex items-center justify-center mt-4 space-x-4">
            <Badge variant="secondary" className="flex items-center space-x-1">
              <Star className="h-3 w-3" />
              <span>Evidence-Based</span>
            </Badge>
            <Badge variant="secondary" className="flex items-center space-x-1">
              <Shield className="h-3 w-3" />
              <span>Secure</span>
            </Badge>
          </div>
        </Card>

        {/* AmIOK Score Explanation */}
        <AmIOKScoreExplanation />

        {/* Quick Actions */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-foreground">Quick Actions</h3>
          <div className="grid gap-3">
            {quickActions.map((action, index) => <Card key={index} className={`p-4 cursor-pointer transition-colors ${action.urgent ? 'border-destructive bg-destructive/5 hover:bg-destructive/10' : 'hover:bg-muted/50'}`} onClick={action.action}>
                <div className="flex items-center space-x-3">
                  <action.icon className={`h-5 w-5 ${action.urgent ? 'text-destructive' : 'text-primary'}`} />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{action.label}</h4>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </Card>)}
          </div>
        </div>

        {/* Trust Indicators */}
        <TrustBadgeDisplay variant="hero" className="mt-6" />

        {/* Clinical Disclaimer */}
        <ClinicalDisclaimerPanel variant="emergency" className="mt-6" />
      </main>
    </div>;
};
export default Index;